import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';

/**
 * Service for session management and device tracking
 */

export const sessionService = {
  /**
   * Create new session entry
   */
  async createSession(sessionData) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('user_sessions')
        ?.insert([{
          user_id: user?.id,
          device_type: sessionData?.deviceType || 'unknown',
          device_name: sessionData?.deviceName,
          device_fingerprint: sessionData?.deviceFingerprint,
          ip_address: sessionData?.ipAddress,
          user_agent: sessionData?.userAgent,
          location: sessionData?.location,
          expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()
        }])
        ?.select()
        ?.single();

      if (error) {
        logger?.error('Failed to create session', error, { requestId });
        return { data: null, error };
      }

      logger?.audit('SESSION_CREATED', user?.id, { sessionId: data?.id });
      return { data, error: null };
    } catch (error) {
      logger?.error('Session creation error', error, { requestId });
      return { data: null, error: { message: 'Failed to create session' } };
    }
  },

  /**
   * Get all active sessions for current user
   */
  async getActiveSessions() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('user_sessions')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('status', 'active')
        ?.order('last_activity_at', { ascending: false });

      if (error) {
        logger?.error('Failed to fetch sessions', error, { requestId });
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      logger?.error('Session fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch sessions' } };
    }
  },

  /**
   * Revoke a specific session
   */
  async revokeSession(sessionId, reason = 'User initiated') {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('user_sessions')
        ?.update({
          status: 'revoked',
          revoked_at: new Date()?.toISOString(),
          revoked_reason: reason
        })
        ?.eq('id', sessionId)
        ?.eq('user_id', user?.id)
        ?.select()
        ?.single();

      if (error) {
        logger?.error('Failed to revoke session', error, { requestId });
        return { data: null, error };
      }

      logger?.audit('SESSION_REVOKED', user?.id, { sessionId, reason });
      return { data, error: null };
    } catch (error) {
      logger?.error('Session revocation error', error, { requestId });
      return { data: null, error: { message: 'Failed to revoke session' } };
    }
  },

  /**
   * Revoke all sessions except current
   */
  async revokeAllOtherSessions(currentSessionId) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('user_sessions')
        ?.update({
          status: 'revoked',
          revoked_at: new Date()?.toISOString(),
          revoked_reason: 'Revoked all other sessions'
        })
        ?.eq('user_id', user?.id)
        ?.neq('id', currentSessionId)
        ?.eq('status', 'active')
        ?.select();

      if (error) {
        logger?.error('Failed to revoke sessions', error, { requestId });
        return { data: null, error };
      }

      logger?.audit('ALL_SESSIONS_REVOKED', user?.id, { count: data?.length });
      return { data, error: null };
    } catch (error) {
      logger?.error('Bulk session revocation error', error, { requestId });
      return { data: null, error: { message: 'Failed to revoke sessions' } };
    }
  },

  /**
   * Update session activity
   */
  async updateSessionActivity(sessionId) {
    try {
      const { error } = await supabase
        ?.from('user_sessions')
        ?.update({ last_activity_at: new Date()?.toISOString() })
        ?.eq('id', sessionId);

      return { error };
    } catch (error) {
      return { error: { message: 'Failed to update session activity' } };
    }
  }
};

export default sessionService;